import {
  ChartBarSquareIcon,
  ClipboardDocumentCheckIcon,
  DocumentDuplicateIcon,
  IdentificationIcon,
  LockClosedIcon,
  NewspaperIcon,
  RectangleStackIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import {
  ClaimSharePaymentsPage,
  ClaimsPage,
  ClassOfBusinessPage,
  CreateClosingPage,
  CreateSlipPage,
  CreditNotesPage,
  DashboardPage,
  EmployeesPage,
  EndorsementPage,
  EndorsementsPage,
  InsurerPage,
  InsurerPaymentsPage,
  InsurersPage,
  NotificationsPage,
  OfferPage,
  ProfilePage,
  ReInsurerPage,
  ReInsurerPaymentsPage,
  ReInsurersPage,
  ResetPasswordPage,
  SignInPage,
  ViewClaimSharesPage,
  ViewClaimsPage,
} from "pages";
import { Outlet, Route } from "react-location";

import { DocumentPlusIcon } from "@heroicons/react/24/solid";
import { UserPermission } from "_graphql/cache/auth";

export type RouteProps = Omit<Route, "children"> & {
  withPermissions?: UserPermission[];
  navigation?: boolean;
  sidebar?: { label: string; icon: any };
  children?: RouteProps[];
};

const routes: RouteProps[] = [
  {
    path: "/",
    element: <Outlet />,
    sidebar: {
      label: "Dashboard",
      icon: ChartBarSquareIcon,
    },
    meta: {
      layout: "App",
      breadcrumb: () => "Dashboard",
      section: "General",
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <DashboardPage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
    ],
  },
  {
    path: "create-slip",
    element: <Outlet />,
    meta: {
      layout: "App",
      breadcrumb: () => "Create Slip",
      section: "Facultative Businesses",
    },
    sidebar: {
      label: "Create Slip",
      icon: DocumentPlusIcon,
    },
    withPermissions: [
      "*:*",
      "offers:*",
      "offers:view",
      "offers:manage",
      "offers:create",
      "offers:update",
    ],
    children: [
      {
        id: "all",
        path: "/",
        element: <CreateSlipPage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
      {
        path: ":offer/view",
        element: <OfferPage />,
        meta: {
          layout: "App",
          section: "General",
          breadcrumb: () => `View Slip`,
        },
      },
    ],
  },
  {
    path: "create-closing",
    element: <Outlet />,
    withPermissions: [
      "*:*",
      "offers:*",
      "offers:view",
      "offers:manage",
      "offers:create",
      "offers:update",
      "offers:reinstate",
      "offers:approve",
      "offers:payment",
    ],
    meta: {
      layout: "App",
      breadcrumb: () => "Create Closing",
      section: "Facultative Businesses",
    },
    sidebar: {
      label: "Create Closing",
      icon: LockClosedIcon,
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <CreateClosingPage />,
        meta: {
          layout: "App",
          section: "General",
        },
        children: [],
      },
      {
        path: ":offer/credit-notes",
        element: <CreditNotesPage />,
        meta: {
          layout: "App",
          section: "General",
          breadcrumb: () => "View Credit Notes",
        },
      },
    ],
  },
  {
    path: "endorsements",
    element: <Outlet />,
    withPermissions: [
      "*:*",
      "endorsements:*",
      "endorsements:view",
      "endorsements:manage",
      "endorsements:create",
      "endorsements:update",
    ],
    meta: {
      layout: "App",
      breadcrumb: () => "Endorsements",
      section: "Facultative Businesses",
    },
    sidebar: {
      label: "Endorsements",
      icon: ClipboardDocumentCheckIcon,
    },
    children: [
      {
        path: "/",
        element: <EndorsementsPage />,
        meta: {
          layout: "App",
          section: "Facultative Businesses",
        },
        children: [],
      },
      {
        path: ":endorsement/endorsement/:endorsement_policy_number",
        element: <EndorsementPage />,
        meta: {
          layout: "App",
          section: "Facultative Businesses",
          breadcrumb: ({
            endorsement_policy_number,
          }: {
            endorsement_policy_number: string;
          }) => endorsement_policy_number,
        },
        children: [],
      },
    ],
  },
  {
    path: "insurers",
    withPermissions: [
      "*:*",
      "insurers:*",
      "insurers:manage",
      "insurers:create",
      "insurers:update",
    ],
    element: <Outlet />,
    meta: {
      layout: "App",
      breadcrumb: () => "Insurers",
      section: "Clients",
    },
    sidebar: {
      label: "Insurers",
      icon: UsersIcon,
    },
    children: [
      {
        path: "/",
        element: <InsurersPage />,
        meta: {
          layout: "App",
          section: "Clients",
        },
        children: [],
      },
      {
        path: ":insurer/view",
        element: <Outlet />,
        meta: {
          layout: "App",
          section: "Clients",
          breadcrumb: () => "View Insurer",
        },
        children: [
          {
            path: "/",
            element: <InsurerPage />,
            meta: {
              layout: "App",
              section: "Clients",
            },
          },
          {
            path: ":offer/payments",
            element: <Outlet />,
            meta: {
              layout: "App",
              section: "Clients",
              breadcrumb: () => "View Payments",
            },
            children: [
              {
                path: "/",
                element: <InsurerPaymentsPage />,
                meta: {
                  layout: "App",
                  section: "Clients",
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "re-insurers",
    element: <Outlet />,
    withPermissions: [
      "*:*",
      "reinsurers:*",
      "reinsurers:manage",
      "reinsurers:create",
      "reinsurers:update",
    ],
    meta: {
      layout: "App",
      breadcrumb: () => "Re-Insurers",
      section: "Clients",
    },
    sidebar: {
      label: "Re-Insurers",
      icon: UserGroupIcon,
    },
    children: [
      {
        path: "/",
        element: <ReInsurersPage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
      {
        path: ":reInsurer",
        element: <Outlet />,
        meta: {
          layout: "App",
          section: "General",
          breadcrumb: () => "View Re-Insurer",
        },
        children: [
          {
            path: "/",
            element: <ReInsurerPage />,
            meta: {
              layout: "App",
              section: "Clients",
            },
          },
          {
            path: ":offer_participant/payments/:offer",
            element: <ReInsurerPaymentsPage />,
            meta: {
              layout: "App",
              section: "Clients",
              breadcrumb: () => "View Re-Insurer Payments",
            },
          },
        ],
      },
    ],
  },
  {
    path: "class-of-business",
    element: <Outlet />,
    meta: {
      layout: "App",
      breadcrumb: () => "Setup Business",
      section: "Facultative Businesses",
    },
    sidebar: {
      label: "Setup Business",
      icon: DocumentDuplicateIcon,
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <ClassOfBusinessPage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
    ],
    withPermissions: [
      "*:*",
      "business:*",
      "business:manage",
      "business:create",
      "business:delete",
      "business:update",
    ],
  },
  {
    path: "claims",
    element: <Outlet />,
    meta: {
      layout: "App",
      breadcrumb: () => "Facultative Claims",
      section: "Facultative Businesses",
    },
    sidebar: {
      label: "Facultative Claims",
      icon: NewspaperIcon,
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <ClaimsPage />,
        meta: {
          layout: "App",
          section: "Facultative Businesses",
        },
      },
      {
        id: "all-claims",
        path: "/:offer/view",
        element: <Outlet />,
        meta: {
          layout: "App",
          section: "General",
          breadcrumb: () => "View Claims",
        },
        children: [
          {
            path: "/",
            element: <ViewClaimsPage />,
            meta: {
              layout: "App",
              section: "General",
              // breadcrumb: () => "View Shares",
            },
          },
          {
            id: "all-claims-claim",
            path: ":claim/view",
            element: <Outlet />,
            meta: {
              layout: "App",
              section: "General",
              breadcrumb: () => "View Shares",
            },
            children: [
              {
                path: "/",
                element: <ViewClaimSharesPage />,
                meta: {
                  layout: "App",
                  section: "General",
                  // breadcrumb: () => "View Shares",
                },
              },
              {
                path: ":share/payments",
                element: <ClaimSharePaymentsPage />,
                meta: {
                  layout: "App",
                  section: "General",
                  breadcrumb: () => "View Share Payments",
                },
              },
            ],
          },
        ],
      },
    ],
    withPermissions: [
      "*:*",
      "claims:*",
      "claims:manage",
      "claims:create",
      "claims:delete",
      "claims:update",
    ],
  },
  {
    path: "employees",
    element: <Outlet />,
    meta: {
      layout: "App",
      breadcrumb: () => "Employees",
      section: "Others",
    },
    sidebar: {
      label: "Employees",
      icon: IdentificationIcon,
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <EmployeesPage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
    ],
    withPermissions: [
      "*:*",
      "employees:*",
      "employees:create",
      "employees:manage",
      "employees:delete",
      "employees:reset",
      "employees:update",
    ],
  },
  {
    path: "notifications",
    element: <Outlet />,
    meta: {
      layout: "App",
      breadcrumb: () => "System Notifications",
      section: "Notifcations",
    },
    sidebar: {
      label: "System Notifications",
      icon: RectangleStackIcon,
    },
    children: [
      {
        path: "/",
        element: <NotificationsPage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
    ],
  },
  {
    path: "profile",
    element: <Outlet />,
    meta: {
      layout: "App",
      breadcrumb: () => "Profile",
      section: "General",
    },
    children: [
      {
        path: "/",
        element: <ProfilePage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
    ],
  },
  {
    path: "cpanel-login",
    element: <SignInPage />,
    meta: {
      layout: "Auth",
      breadcrumb: () => "Auth",
    },
  },
  {
    path: "cpanel-reset-password",
    element: <ResetPasswordPage />,
    meta: {
      layout: "Auth",
      breadcrumb: () => "Auth",
    },
  },
];

export default routes;
